import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DERMA_DRUGS, PRESCRIPTION_FREQUENCIES, DRUG_INSTRUCTIONS, DRUG_ROUTES } from "@/lib/constants";
import { Plus, Trash2, Pill } from "lucide-react";

function DrugRow({ item, index, onChange, onRemove }) {
  const set = (k, v) => onChange(index, { ...item, [k]: v });

  return (
    <div className="grid grid-cols-12 items-start gap-2 rounded-lg border border-navy/10 bg-white p-2.5">
      <div className="col-span-12 sm:col-span-3">
        <label className="text-[10px] text-muted-foreground">Drug Name</label>
        <Input value={item.drug_name} onChange={(e) => set("drug_name", e.target.value)} placeholder="Medicine" className="h-8 text-sm" />
      </div>
      <div className="col-span-4 sm:col-span-1">
        <label className="text-[10px] text-muted-foreground">Dosage</label>
        <Input value={item.dosage || ""} onChange={(e) => set("dosage", e.target.value)} placeholder="10mg" className="h-8 text-sm" />
      </div>
      <div className="col-span-4 sm:col-span-2">
        <label className="text-[10px] text-muted-foreground">Frequency</label>
        <Select value={item.frequency || ""} onValueChange={(v) => set("frequency", v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Freq" /></SelectTrigger>
          <SelectContent>
            {PRESCRIPTION_FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-4 sm:col-span-2">
        <label className="text-[10px] text-muted-foreground">Duration</label>
        <Input value={item.duration || ""} onChange={(e) => set("duration", e.target.value)} placeholder="7 days" className="h-8 text-sm" />
      </div>
      <div className="col-span-5 sm:col-span-2">
        <label className="text-[10px] text-muted-foreground">Instructions</label>
        <Select value={item.instructions || ""} onValueChange={(v) => set("instructions", v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            {DRUG_INSTRUCTIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-5 sm:col-span-1">
        <label className="text-[10px] text-muted-foreground">Route</label>
        <Select value={item.route || "oral"} onValueChange={(v) => set("route", v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DRUG_ROUTES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 sm:col-span-1 flex items-end justify-end pb-0.5">
        <Button size="icon" variant="ghost" onClick={() => onRemove(index)} className="h-8 w-8 text-red-400 hover:text-red-600">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function PrescriptionBuilder({ items, onChange, advice, onAdviceChange }) {
  const rows = items || [];
  const [showQuick, setShowQuick] = useState(false);

  const addRow = (drug = null) => {
    const empty = { drug_name: "", dosage: "", frequency: "", duration: "", instructions: "", route: "oral" };
    if (drug) {
      empty.drug_name = drug.name;
      empty.dosage = drug.defaultDosage;
      empty.frequency = drug.defaultFreq;
      empty.route = drug.route;
    }
    onChange([...rows, empty]);
  };

  const updateRow = (idx, updated) => {
    const next = [...rows];
    next[idx] = updated;
    onChange(next);
  };

  const removeRow = (idx) => {
    onChange(rows.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      {/* Quick-add common drugs */}
      <div>
        <button
          onClick={() => setShowQuick(!showQuick)}
          className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gold hover:underline"
        >
          <Pill className="h-3.5 w-3.5" />
          {showQuick ? "Hide" : "Show"} common drugs
        </button>
        {showQuick && (
          <div className="flex flex-wrap gap-1.5">
            {DERMA_DRUGS.map((d) => (
              <button
                key={d.name}
                onClick={() => { addRow(d); setShowQuick(false); }}
                className="rounded-full border border-navy/10 bg-cream/60 px-2.5 py-1 text-[11px] font-medium text-navy hover:bg-gold/10 hover:border-gold/30"
              >
                {d.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Drug rows */}
      <div className="space-y-2">
        {rows.map((item, i) => (
          <DrugRow key={i} item={item} index={i} onChange={updateRow} onRemove={removeRow} />
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={() => addRow()} className="border-navy/20 text-navy">
        <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Medicine
      </Button>

      {/* Advice */}
      <div className="space-y-1.5">
        <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Advice / Instructions</label>
        <Textarea
          rows={3}
          placeholder="Skincare routine, diet advice, follow-up instructions..."
          value={advice || ""}
          onChange={(e) => onAdviceChange(e.target.value)}
        />
      </div>
    </div>
  );
}
